import React, { FC, Fragment, useState, useContext, useCallback } from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import AddEditProductionPlanTemplateDialog from './add-edit-production-plan-template-dialog/add-edit-production-plan-template-dialog.component';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import { ProductionPlanningTemplate } from 'models/organization';
import { useDepartments } from 'context/departments-context';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { usePlanningTemplate } from 'hooks/usePlanningTemplate';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'text', width: 100 },
    { type: 'text', width: 100 },
    { type: 'button', width: 80 },
];

type ProductionPlanTemplateListProps = {
    addLabel: string;
    templates: ProductionPlanningTemplate[];
    loading: boolean;
    deleteTemplateLoading: boolean;
    onDelete: (templateId: string) => Promise<unknown>;
    onEdit: (template: ProductionPlanningTemplate) => void;
    onCopy: (template: ProductionPlanningTemplate) => void;
    onAdd: () => void;
    getStepsLabel: (template: ProductionPlanningTemplate) => string;
};

const ProductionPlanTemplateList: FC<ProductionPlanTemplateListProps> = ({
    addLabel,
    templates,
    loading,
    deleteTemplateLoading,
    onDelete,
    onEdit,
    onCopy,
    onAdd,
    getStepsLabel,
}) => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

    const handleConfirmDelete = useCallback(async () => {
        if (!deleteConfirmId) return;
        const result = await onDelete(deleteConfirmId);
        setDeleteConfirmId(null);
        if (result) {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:PRODUCTION_PLAN_TEMPLATE_DELETED_SUCCESSFULLY'),
            });
        }
    }, [deleteConfirmId, onDelete, setAppToasterContent, translate]);

    return (
        <Box mb={4}>
            <Box mb={1}>
                <Button startIcon={<AddIcon />} onClick={onAdd}>
                    {addLabel}
                </Button>
            </Box>
            <Paper variant="outlined">
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('lab-settings:PRODUCTION_PLAN_TEMPLATE')}</TableCell>
                                <TableCell>{translate('lab-settings:WORKFLOW_STEPS')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && <SkeletonTable columns={skeletonColumns} rows={3} renderRowsOnly={true} />}
                            {templates.length > 0 &&
                                templates.map((row, index) => (
                                    <TableRow key={index} hover onClick={() => onEdit(row)} sx={{ cursor: 'pointer' }}>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{getStepsLabel(row)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={translate('common:COPY')}>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onCopy(row);
                                                    }}
                                                >
                                                    <ContentCopyIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={translate('common:DELETE')}>
                                                <IconButton
                                                    disabled={deleteTemplateLoading}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteConfirmId(row._id || '');
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {templates.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        {translate('lab-settings:NO_PRODUCTION_PLAN_TEMPLATES_FOUND')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <ConfirmDialog
                open={!!deleteConfirmId}
                handleClose={() => setDeleteConfirmId(null)}
                handleConfirm={handleConfirmDelete}
                dialogTitle={translate('common:DELETE')}
                dialogContent={translate('lab-settings:CONFIRM_DELETE_PRODUCTION_PLAN_TEMPLATE')}
                confirmBtnLabel={translate('common:DELETE')}
            />
        </Box>
    );
};

const ProductionPlanTemplates: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'workflow', 'common']);
    const { departments } = useDepartments();

    const {
        planningTemplates,
        planningTemplatesLoading,
        deletePlanningTemplate,
        deleteTemplateLoading: deletePlanningLoading,
        addPlanningTemplate,
        editPlanningTemplate,
        addTemplateLoading,
        editTemplateLoading,
    } = usePlanningTemplate();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<ProductionPlanningTemplate | null>(null);

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedTemplate(null);
    };

    const openDialog = (template: ProductionPlanningTemplate | null = null) => {
        setSelectedTemplate(template);
        setDialogOpen(true);
    };

    return (
        <Fragment>
            {departments.length === 0 && (
                <Box mt={1} textAlign="right">
                    <Alert severity="info">{`${translate('lab-settings:PLEASE_ADD_DEPARTMENTS')}`}</Alert>
                </Box>
            )}
            {departments.length > 0 && (
                <>
                    <ProductionPlanTemplateList
                        addLabel={translate('lab-settings:ADD_PRODUCTION_PLAN')}
                        templates={planningTemplates}
                        loading={planningTemplatesLoading}
                        deleteTemplateLoading={deletePlanningLoading}
                        onDelete={(id: string) => deletePlanningTemplate({ templateId: id })}
                        onEdit={(template: ProductionPlanningTemplate) => openDialog(template)}
                        onCopy={(template: ProductionPlanningTemplate) =>
                            openDialog({
                                ...template,
                                _id: undefined,
                                name: `${translate('lab-settings:COPY_OF')} ${template.name}`,
                            })
                        }
                        onAdd={() => openDialog()}
                        getStepsLabel={(template: ProductionPlanningTemplate) =>
                            template.workflowSteps.map((step) => step.name).join(', ')
                        }
                    />

                    <AddEditProductionPlanTemplateDialog
                        productionTemplate={selectedTemplate ?? undefined}
                        open={dialogOpen}
                        onDialogClose={handleDialogClose}
                        addPlanningTemplate={addPlanningTemplate}
                        editPlanningTemplate={editPlanningTemplate}
                        addTemplateLoading={addTemplateLoading}
                        editTemplateLoading={editTemplateLoading}
                    />
                </>
            )}
        </Fragment>
    );
};

export default ProductionPlanTemplates;
